import {
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowSync20Regular,
  Edit20Regular,
} from "@fluentui/react-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { refetchDocumentParcels } from "@/api/documents";
import { refetchEmailParcels } from "@/api/emails";
import { ApiError } from "@/lib/apiClient";

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body;
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as Record<string, unknown>).detail;
      if (typeof detail === "string") return detail;
    }
    return err.message;
  }
  return err instanceof Error ? err.message : "Refetch failed";
}

interface ParcelSummaryRowProps {
  emailId: string;
  documentId?: string;
  selectedParcelNumber: number | null;
  candidateCount: number;
  disabled?: boolean;
  onChange: () => void;
}

/**
 * Inline parcel summary + actions, shared by ReviewLayout (per-document)
 * and EmailDetailDrawer (per-email). Refetch resolves the trade key from
 * the doc's effective trade when called per-document; otherwise falls
 * back to the email-level fetch. The {@link onChange} callback opens
 * the relevant ParcelSelector with the right scope preselected.
 */
export function ParcelSummaryRow({
  emailId,
  documentId,
  selectedParcelNumber,
  candidateCount,
  disabled,
  onChange,
}: ParcelSummaryRowProps) {
  const qc = useQueryClient();
  const refetch = useMutation<unknown, Error, void>({
    mutationFn: () =>
      documentId
        ? refetchDocumentParcels(documentId)
        : refetchEmailParcels(emailId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["email", emailId] });
      if (documentId) {
        void qc.invalidateQueries({ queryKey: ["document", documentId] });
      }
    },
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: 8,
        background: tokens.colorNeutralBackground2,
        border: `1px solid ${tokens.colorNeutralStroke3}`,
        borderRadius: 4,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <Text size={200}>
          {selectedParcelNumber !== null
            ? `Selected parcel: ${selectedParcelNumber}`
            : "No parcel selected yet"}
          {candidateCount > 0 ? ` · ${candidateCount} candidate(s)` : ""}
        </Text>
        <div style={{ flex: 1 }} />
        <Button
          size="small"
          icon={<Edit20Regular />}
          onClick={onChange}
          disabled={disabled}
        >
          Change parcel
        </Button>
        <Button
          size="small"
          icon={
            refetch.isPending ? <Spinner size="tiny" /> : <ArrowSync20Regular />
          }
          onClick={() => refetch.mutate()}
          disabled={disabled || refetch.isPending}
          title="Re-query Symphony for parcel candidates"
        >
          {refetch.isPending ? "Refetching…" : "Refetch"}
        </Button>
      </div>
      {refetch.isError ? (
        <MessageBar
          intent={
            refetch.error instanceof ApiError && refetch.error.status === 502
              ? "warning"
              : "error"
          }
        >
          <MessageBarBody>{describeError(refetch.error)}</MessageBarBody>
        </MessageBar>
      ) : null}
    </div>
  );
}
